import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Wrench, BookOpen, Star, Inbox, ArrowUpRight, Plus } from "lucide-react";
import { useAdminAuth } from "@/admin/AdminAuthContext";
import { fetchSummary } from "@/admin/requestsAdminApi";
import { TYPE_LABELS } from "@/admin/requestsLabels";
import { adminListTools, adminListArticles, adminListQuickPicks } from "@/admin/cmsApi";

export default function AdminDashboard() {
  const { admin } = useAdminAuth();
  const displayName =
    admin?.name?.trim() ||
    (admin?.email ? admin.email.split("@")[0] : "Admin");

  const [summary, setSummary] = useState(null);
  const [summaryError, setSummaryError] = useState(false);
  const [toolsCount, setToolsCount] = useState(null);
  const [quickCount, setQuickCount] = useState(null);
  const [articlesCount, setArticlesCount] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [s, t, q, a] = await Promise.all([
          fetchSummary().catch(() => null),
          adminListTools().catch(() => null),
          adminListQuickPicks().catch(() => null),
          adminListArticles().catch(() => null),
        ]);
        if (cancelled) return;
        if (s) setSummary(s); else setSummaryError(true);
        if (t) setToolsCount((t.items || []).length);
        if (q) setQuickCount((q.items || []).length);
        if (a) setArticlesCount((a.items || []).length);
      } catch {
        if (!cancelled) setSummaryError(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const totalNew = summary?.total_new ?? 0;
  const total = summary?.total ?? 0;

  return (
    <div data-testid="admin-dashboard">
      <div className="sas-eyebrow mb-2">Dashboard</div>
      <h1 className="sas-h1" data-testid="admin-dashboard-welcome">
        Welcome, {displayName}.
      </h1>
      <p className="sas-body mt-3 max-w-2xl text-[15px]">
        This is your admin console for the Syrian AI Society website. Manage the
        public tools directory, articles, featured quick picks, and incoming
        requests from the public contact forms.
      </p>

      {/* Primary management tiles */}
      <section className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="admin-dashboard-tiles">
        <ManageTile
          to="/admin/tools"
          icon={Wrench}
          title="Tools"
          body="Add, edit, delete, and categorise the free AI tools shown on /tools."
          counter={toolsCount === null ? null : `${toolsCount} tools`}
          actionLabel="Open"
          secondary={{ to: "/admin/tools/new", label: "New", icon: Plus }}
          testId="admin-tile-tools"
        />
        <ManageTile
          to="/admin/articles"
          icon={BookOpen}
          title="Articles"
          body="Publish and update Arabic-first articles with bilingual bodies and SEO."
          counter={articlesCount === null ? null : `${articlesCount} articles`}
          actionLabel="Open"
          secondary={{ to: "/admin/articles/new", label: "New", icon: Plus }}
          testId="admin-tile-articles"
        />
        <ManageTile
          to="/admin/tools/quick-picks"
          icon={Star}
          title="Quick picks (featured)"
          body="Curate the “Start here” tiles at the top of the public tools page."
          counter={quickCount === null ? null : `${quickCount} featured`}
          actionLabel="Manage"
          testId="admin-tile-quick-picks"
        />
        <ManageTile
          to="/admin/requests"
          icon={Inbox}
          title="Requests"
          body="Submissions from the internal contact forms, grouped by category."
          counter={summary ? `${totalNew} new · ${total} total` : summaryError ? "Unavailable" : "Loading…"}
          actionLabel="Open"
          testId="admin-tile-requests"
        />
      </section>

      {/* Requests detail breakdown */}
      {summary && (
        <section
          className="mt-8 rounded-xl border border-[color:var(--sas-line)] bg-white p-5 md:p-6"
          data-testid="admin-dashboard-requests-card"
        >
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <h2 className="text-[13px] font-semibold text-[color:var(--sas-ink)] uppercase tracking-wide">
              Requests by category
            </h2>
            <Link
              to="/admin/requests"
              className="inline-flex items-center gap-1.5 text-xs font-medium text-[color:var(--sas-ink)] px-3 py-1.5 rounded-full border border-[color:var(--sas-line)] hover:border-[color:var(--sas-ink)] transition-colors"
              data-testid="admin-dashboard-open-requests"
            >
              Inbox <ArrowUpRight size={14} aria-hidden />
            </Link>
          </div>
          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
            {(summary.by_type || []).map((row) => (
              <Link
                key={row.type}
                to={`/admin/requests?type=${row.type}`}
                className="rounded-lg border border-[color:var(--sas-line)] p-3 hover:border-[color:var(--sas-ink)] transition-colors bg-white"
                data-testid={`admin-dashboard-requests-${row.type}`}
              >
                <div className="text-[11px] font-semibold tracking-wide uppercase text-[color:var(--sas-ink-2)]">
                  {TYPE_LABELS[row.type] || row.label}
                </div>
                <div className="mt-2 flex items-baseline gap-2">
                  <span className="text-[22px] font-semibold text-[color:var(--sas-ink)]">
                    {row.count}
                  </span>
                  {row.new_count > 0 && (
                    <span className="text-[11px] font-medium text-[#991b1b] bg-[#fee2e2] px-1.5 py-0.5 rounded-full">
                      {row.new_count} new
                    </span>
                  )}
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      <section
        className="mt-8 rounded-xl border border-[color:var(--sas-line)] p-5 bg-[color:var(--sas-accent-soft)]/30"
        data-testid="admin-session-note"
      >
        <h3 className="text-sm font-semibold text-[color:var(--sas-ink)]">Signed in as</h3>
        <p className="sas-body mt-1 text-[13.5px]">
          {admin?.email}
          <span className="mx-2 opacity-50" aria-hidden>·</span>
          Session valid for 7 days · verified server-side on every request.
        </p>
      </section>
    </div>
  );
}

function ManageTile({ to, icon: Icon, title, body, counter, actionLabel, secondary, testId }) {
  return (
    <article
      className="rounded-xl border border-[color:var(--sas-line)] bg-white p-5 flex flex-col"
      data-testid={testId}
    >
      <div className="flex items-start justify-between gap-3">
        <div
          className="w-10 h-10 rounded-full flex items-center justify-center"
          style={{ background: "var(--sas-accent-soft)", color: "var(--sas-accent)" }}
          aria-hidden
        >
          <Icon size={18} />
        </div>
        {counter !== null && (
          <span className="text-[11px] text-[color:var(--sas-ink-2)] font-medium">{counter}</span>
        )}
      </div>
      <h3 className="text-[15.5px] font-semibold text-[color:var(--sas-ink)] mt-4">{title}</h3>
      <p className="sas-body mt-1.5 text-[13.5px] flex-1">{body}</p>
      <div className="mt-4 flex items-center gap-2 flex-wrap">
        <Link
          to={to}
          className="inline-flex items-center gap-1.5 text-[12.5px] font-medium text-[color:var(--sas-ink)] px-3 py-1.5 rounded-full border border-[color:var(--sas-line)] hover:border-[color:var(--sas-ink)] transition-colors"
          data-testid={`${testId}-open`}
        >
          {actionLabel} <ArrowUpRight size={13} aria-hidden />
        </Link>
        {secondary && (
          <Link
            to={secondary.to}
            className="inline-flex items-center gap-1.5 text-[12.5px] text-[color:var(--sas-ink-2)] hover:text-[color:var(--sas-ink)]"
            data-testid={`${testId}-new`}
          >
            <secondary.icon size={13} aria-hidden /> {secondary.label}
          </Link>
        )}
      </div>
    </article>
  );
}
